import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoButton } from '@atlassian/search-common/rovo-navigation/rovo-button';

import type { PrimaryProductKey } from '../../../common/constants/products';
import { serveJiraNinFpsNavigation } from '../../../common/utils/experiments/experiment-exports';
import { useSainJql } from '../../../controllers/smart-answers-intent-store';

import { messages } from './messages';

type SearchPageHeaderProps = {
	primaryProduct?: PrimaryProductKey;
	query?: string;
};

const styles = cssMap({
	headerStyles: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: token('space.050'),
		margin: 'auto',
		width: '100%',
		maxWidth: '100%',
		whiteSpace: 'nowrap',
		paddingTop: token('space.200'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	headerText: {
		marginTop: token('space.0'),
		fontWeight: token('font.weight.regular'),
		alignItems: 'center',
		gap: token('space.100'),
		display: 'flex',
		flex: 1,
		minWidth: '0px',
	},
	headerTitle: {
		fontWeight: token('font.weight.bold'),
		flexShrink: 0,
	},
	headerQuery: {
		flex: 1,
		minWidth: '0px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

export const SearchPageHeader = ({
	primaryProduct,
	query,
}: SearchPageHeaderProps): React.JSX.Element | null => {
	const { formatMessage, locale } = useIntl();
	const sainJql = useSainJql();

	const fpsNinNavigation = useMemo(
		() => serveJiraNinFpsNavigation({ primaryProduct, fireLayerExposure: false, locale }),
		[primaryProduct, locale],
	);

	if (fpsNinNavigation?.cohort !== 'button') {
		return null;
	}

	const showQueryInHeader = Boolean(query) && Boolean(fpsNinNavigation.showQueryInHeader);
	const headerLabel = `${formatMessage(messages.searchResults)}${showQueryInHeader ? ':' : ''}`;

	return (
		<Box xcss={styles.headerStyles}>
			<Box as="h2" xcss={styles.headerText}>
				<Box xcss={styles.headerTitle}>{headerLabel}</Box>
				{showQueryInHeader && <Box xcss={styles.headerQuery}>&#39;{query}&#39;</Box>}
			</Box>
			<RovoButton experience="full-page-search" sainJql={sainJql} />
		</Box>
	);
};
